// Print helpers for the per-module Internal Transfer Report (mirrors Greige Storage).
export const esc = (v) => String(v ?? '').replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c]));

export const fmtDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const PRINT_TRIGGER = '<script>window.onload=function(){try{window.focus();window.print();}catch(e){}};</script>';

const PRINT_STYLE = `
  * { font-family: Arial, Helvetica, sans-serif; }
  body { margin: 24px; color: #111; }
  h1 { font-size: 18px; margin: 0 0 2px; }
  .sub { font-size: 12px; color: #555; margin-bottom: 14px; }
  table.meta { width: 100%; font-size: 12px; margin-bottom: 14px; border-collapse: collapse; }
  table.meta td { padding: 2px 6px; }
  table.meta .k { color: #666; width: 90px; }
  table.grid { width: 100%; border-collapse: collapse; font-size: 12px; }
  table.grid th, table.grid td { border: 1px solid #333; padding: 6px 8px; text-align: left; vertical-align: top; }
  table.grid th { background: #f0f0f0; }
  td.c, th.c { text-align: center; }
  .b { font-weight: bold; }
  tfoot td { font-weight: bold; background: #fafafa; }
  @media print { body { margin: 10mm; } }
`;

// Open synchronously inside the click so popup blockers don't kill it after an await.
export const openBlankPrintWindow = () => {
  const win = window.open('', '_blank');
  if (win) {
    win.document.write('<!doctype html><html><body style="font-family:Arial;padding:28px;color:#555;font-size:14px">Preparing print… please wait.</body></html>');
    win.document.close();
  }
  return win;
};

export const fillPrintWindow = (win, html) => {
  if (!win || win.closed) return;
  win.document.open();
  win.document.write(html);
  win.document.close();
  win.focus();
};

export const failPrintWindow = (win, msg) => fillPrintWindow(
  win, `<!doctype html><html><body style="font-family:Arial;padding:28px;color:#b91c1c;font-size:14px">${esc(msg)}</body></html>`,
);

const page = (title, body) => `<!doctype html><html><head><meta charset="utf-8"><title>${esc(title)}</title>
  <style>${PRINT_STYLE}</style></head><body>${body}${PRINT_TRIGGER}</body></html>`;

const metaTable = (t, countLabel, count) => `<table class="meta">
  <tr><td class="k">Transfer ID</td><td><b>${esc(t.transfer_id || '-')}</b></td><td class="k">Date</td><td>${esc(fmtDate(t.date))}</td></tr>
  <tr><td class="k">From</td><td>${esc(t.from_section)}</td><td class="k">To</td><td>${esc(t.to_section)}</td></tr>
  <tr><td class="k">Staff</td><td>${esc(t.staff)}</td><td class="k">${esc(countLabel)}</td><td>${count}</td></tr>
  <tr><td class="k">ERP Entry</td><td>${esc(t.erp_entry_no || '—')}</td><td class="k">Remarks</td><td>${esc(t.remarks || '—')}</td></tr>
</table>`;

const numCmp = (a, b) => String(a).localeCompare(String(b), undefined, { numeric: true });

export const buildTransferSheetHtml = (t, moduleName) => {
  const groups = {};
  (t.bundles || []).forEach((b) => {
    const key = [b.sort_no, b.design_name, b.shade, b.size].join('||');
    if (!groups[key]) groups[key] = { sort_no: b.sort_no || '—', design_name: b.design_name, shade: b.shade, size: b.size, bundles: 0, qty: 0, unit: b.qty_unit };
    groups[key].bundles += 1;
    groups[key].qty += Number(b.qty || 0);
  });
  const rows = Object.values(groups).sort((a, b) => numCmp(a.sort_no, b.sort_no) || numCmp(a.design_name, b.design_name));
  const totalQty = rows.reduce((s, r) => s + r.qty, 0);
  const body = rows.map((r) => `<tr><td>${esc(r.sort_no)}</td><td>${esc(r.design_name)}</td><td>${esc(r.shade)}</td>
    <td>${esc(r.size)}</td><td class="c">${r.bundles}</td><td class="c b">${esc(r.qty)}${r.unit === 'Mtr' ? ' m' : ''}</td></tr>`).join('');
  return page(`Internal Transfer ${t.transfer_id}`, `<h1>Internal Transfer Sheet</h1>
    <div class="sub">Minister White — ${esc(moduleName)}</div>${metaTable(t, 'Lines', rows.length)}
    <table class="grid"><thead><tr><th>Sort No</th><th>Design</th><th>Shade</th><th>Size</th><th class="c">Bundles</th><th class="c">Qty</th></tr></thead>
    <tbody>${body}</tbody><tfoot><tr><td colspan="4" style="text-align:right">Total</td><td class="c">${(t.bundles || []).length}</td><td class="c">${totalQty}</td></tr></tfoot></table>`);
};

export const buildBundleWiseHtml = (t, moduleName) => {
  const rows = [...(t.bundles || [])].sort((a, b) => numCmp(a.barcode, b.barcode));
  const totalQty = rows.reduce((s, r) => s + Number(r.qty || 0), 0);
  const body = rows.map((r, i) => `<tr><td class="c">${i + 1}</td><td>${esc(r.barcode)}</td><td>${esc(r.sort_no)}</td>
    <td>${esc(r.design_name)}</td><td>${esc(r.shade)}</td><td>${esc(r.size)}</td><td class="c b">${esc(r.qty ?? 0)}${r.qty_unit === 'Mtr' ? ' m' : ''}</td></tr>`).join('');
  return page(`Bundle-Wise ${t.transfer_id}`, `<h1>Bundle-Wise Transfer Sheet</h1>
    <div class="sub">Minister White — ${esc(moduleName)}</div>${metaTable(t, 'Bundles', rows.length)}
    <table class="grid"><thead><tr><th class="c">#</th><th>Bundle No</th><th>Sort No</th><th>Design</th><th>Shade</th><th>Size</th><th class="c">Qty</th></tr></thead>
    <tbody>${body}</tbody><tfoot><tr><td colspan="6" style="text-align:right">Total</td><td class="c">${totalQty}</td></tr></tfoot></table>`);
};

export const buildTransferReportHtml = (rows, moduleName, rangeTxt, totalCount) => {
  const totBundles = rows.reduce((s, r) => s + Number(r.bundle_count || 0), 0);
  const totQty = rows.reduce((s, r) => s + Number(r.qty || 0), 0);
  const body = rows.map((r, i) => `<tr><td class="c">${i + 1}</td><td>${esc(r.transfer_id)}</td><td>${esc(fmtDate(r.date))}</td>
    <td>${esc(r.to_section)}</td><td class="c">${esc(r.transfer_type)}</td><td class="c b">${esc(r.bundle_count)}</td>
    <td class="c b">${esc(r.qty)}</td><td>${esc(r.staff_name)}${r.staff_id ? ` (${esc(r.staff_id)})` : ''}</td>
    <td>${esc(r.status)}</td><td>${esc(r.remarks)}</td><td>${esc(r.erp_entry_no)}</td></tr>`).join('');
  const more = totalCount > rows.length ? ` · showing first ${rows.length} of ${totalCount}` : '';
  return page(`${moduleName} — Internal Transfer Report`, `<h1>Internal Transfer Report — ${esc(moduleName)}</h1>
    <div class="sub">Minister White · Date range: ${esc(rangeTxt)}${more} · Printed: ${esc(new Date().toLocaleString())}</div>
    <table class="grid"><thead><tr><th class="c">#</th><th>Transfer ID</th><th>Date</th><th>To Section</th><th class="c">Type</th>
    <th class="c">Bundles</th><th class="c">Qty</th><th>Staff</th><th>Status</th><th>Remarks</th><th>ERP Entry No</th></tr></thead>
    <tbody>${body}</tbody><tfoot><tr><td colspan="5" style="text-align:right">Totals (${rows.length} transfers)</td>
    <td class="c">${totBundles}</td><td class="c">${totQty}</td><td colspan="4"></td></tr></tfoot></table>`);
};
